import * as React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    DialogProps
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MBackdrop from './MBackdrop';

interface CDialogProps extends DialogProps {
    /** Explicit height applied to `.MuiDialog-paperScrollPaper`. */
    height?: string | number | null;
}

const CDialog = styled(Dialog)<CDialogProps>(({ height }) => ({
    '& .MuiDialog-paper': {
        maxHeight: 'calc(100% - 64px)', // 64px is the height of the AppBar in MUI
        width: '100%',
        height: 'auto',
        margin: 0,
        borderRadius: 0,
        boxShadow: 'none'
    },
    '& .MuiDialog-scrollPaper': {
        overflow: 'auto'
    },
    '& .MuiDialog-paperScrollPaper': {
        height: height ?? undefined
    }
}));

/**
 * Props for {@link MDialog}.
 */
interface MDialogProps extends Omit<DialogProps, 'title' | 'onClose'> {
    /** Dialog body content. */
    children?: React.ReactNode;
    /** Accessible name applied to the dialog and its close button. */
    name?: string | null;
    /** Title rendered in the `DialogTitle` when {@link customHeader} is not provided. */
    title?: React.ReactNode;
    /** Extra action(s) (e.g. a submit button) rendered before the close button in the footer. */
    action?: React.ReactNode;
    /** Whether the dialog is open. */
    open: boolean;
    /** Called when the dialog requests to be closed (backdrop click, Esc, or Close button). */
    handleClose?: () => void;
    /** Explicit height applied to the dialog paper. */
    height?: string | number | null;
    /** When `true`, disables interaction and shows the loading {@link MBackdrop}. */
    disabled?: boolean;
    /** Message shown in the loading backdrop while {@link disabled} is `true`. */
    msgLoading?: string | null;
    /** Custom node rendered instead of the default `title` in the `DialogTitle`. */
    customHeader?: React.ReactNode;
    /** Whether the footer (actions + close button) is rendered. Defaults to `true`. */
    footer?: boolean;
    /** Label for the default close button. Defaults to `'Close'`. */
    closeLabel?: string;
}

/**
 * A shared MUI {@link Dialog} wrapper providing a consistent header, scrollable
 * content area, footer with a `Close` button plus optional custom {@link action},
 * and a built-in loading {@link MBackdrop} driven by the `disabled` prop.
 */
const MDialog: React.FC<MDialogProps> = ({
    children,
    name = null,
    title,
    action,
    open,
    handleClose,
    height = null,
    disabled = false,
    msgLoading = null,
    customHeader = null,
    footer = true,
    closeLabel = 'Close',
    ...props
}) => {
    return (
        <CDialog
            {...props}
            fullWidth
            height={height}
            aria-label={name ?? undefined}
            open={open}
            onClose={handleClose}
        // TransitionComponent={Transition}
        >
            {customHeader ?
                <DialogTitle>
                    {customHeader}
                </DialogTitle>
                : <DialogTitle>{title}</DialogTitle>
            }
            <Divider />
            <DialogContent>
                {children}
            </DialogContent>
            <Divider />
            {footer && (
                <DialogActions>
                    <Stack width='100%' direction={{ xs: 'column', sm: 'row' }} justifyContent="end" spacing={1}>
                        {action}
                        <Button onClick={handleClose} variant='outlined' aria-label={name ?? undefined}>{closeLabel}</Button>
                    </Stack>
                </DialogActions>
            )}
            <MBackdrop isOpen={disabled} message={msgLoading ?? undefined} />
        </CDialog>
    );
};

export default React.memo(MDialog);
